/**
 * MODULE: _shared/frontend-shell/src/config/nav.ts
 * PURPOSE: Single source of truth for navigation items used by Flowbite sidebar
 * Dependencies: None (pure configuration)
 * Exports: NavItem, mainNavItems, adminNavItems, settingsNavSections, settingsNavItems
 * Side Effects: None
 * Safety: 🟢 SAFE - Configuration only
 * FILE SIZE: ~145 / unlimited (configuration)
 *
 * Agent Notes:
 * - This is the ONLY place to define top-level navigation items
 * - Lab routes are NOT included here (they use shell but no nav)
 * - Super admin routes are in adminNavItems
 * - Main app routes are in mainNavItems
 * - CRM sits after Sales Hub as a single rail link to `/crm`. Territory, Contacts, Deals, Tasks,
 *   and Pipeline are reached from in-app **`CrmTabNav`** (not duplicated in the main sidebar).
 *
 * Recent Changes:
 * - 2026-08-03 [2.2.2024]: Hide **Browser** (`/browser`) from main sidebar — route remains reachable by URL / assistant launch.
 * - 2026-07-05: Removed **Define Your Solutions** sidebar item — product/company setup lives under CRM **Products** / **My Company** tabs (`/crm/products`, `/crm/company`).
 * - 2026-07-01 [2.2.1475]: **Define Your Solutions** nav item (after Sales Hub) → **`/sales-profile?tab=products`** (removed 2026-07-05).
 * - 2026-05-23 [2.2.1135]: **Pages** nav item at **`/pages`** with **`capabilityKey: capability.pages`**; root **`CHANGELOG` [2.2.1135]`**
 * - 2026-05-08 [2.2.884]: CRM **Tasks** hub at **`/crm/tasks`**
 * - 2026-05-01 [2.2.778]: Removed CRM **`children`** from main sidebar (Territory, Contacts, Deals, Pipeline Generation); **`CrmTabNav`** covers those routes inside **`/crm`**; root **`CHANGELOG` [2.2.778]`**
 * - 2026-04-18 [2.2.523]: Tasks nav item removed permanently — root CHANGELOG [2.2.523]
 * - 2026-04-17 [2.2.494]: Tasks (`/tasks`) nav item — inline task board; root CHANGELOG [2.2.494]
 * - 2026-04-10 [2.2.354]: Removed Email (`/email`) and Presentations (`/presentations`) from main sidebar (routes unchanged); root CHANGELOG [2.2.354]
 * - 2026-04-17: Removed AI Templates nav item (flows live under Sales Hub Create tab); root CHANGELOG
 * - 2026-02-19: Enhanced Calendar page with Google Calendar-inspired layout and Flowbite UI components
 * - 2026-02-19: Calendar menu item added between Messaging and Territory
 *
 * - 2026-07-07 [2.2.1527]: **`settingsNavSections`** — **Users & Roles** (`/settings?tab=users`) under Workspace for team-management discoverability (Align Team #7).
 * - 2026-05-09 [2.2.894]: **`settingsNavSections`** (**Plans & usage**: **`/settings/ai-usage`**, **`/settings/billing`**) + flattened **`settingsNavItems`**; root **`CHANGELOG` [2.2.894]`**.
 * - 2026-04-09: settingsNavItems — Email accounts (`/settings/email-accounts`)
 *
 * Current menu order: Assistant, Dashboard, Messaging, Calls, Calendar, Sales Hub, CRM
 * - 2026-02-16: Messaging menu item moved above Territory, below Dashboard
 * - 2026-02-03: Dashboard home link at top; sidebar: Assistant, Dashboard, Territory, Deals, Pipe Gen, Sales Hub
 * - 2026-02-03: Restructured nav for sales rep workflow; Activity in top bar; Settings/Workspaces via user dropdown
 */

export type NavItem = {
  label: string;
  path: string;
  icon?: string;
  roles?: string[];
  section?: "main" | "admin";
  /** When set, item is hidden unless tenant has this capability enabled */
  capabilityKey?: string;
  children?: NavItem[];
};

/**
 * Main navigation items for authenticated users
 * Sidebar: Assistant, Dashboard, Messaging, Calls, Calendar, Sales Hub, CRM
 * Settings (with Workspaces tab) accessed via TopBar user dropdown
 */
export const mainNavItems: NavItem[] = [
  {
    label: "Assistant",
    path: "/langflow-assistant",
    icon: "plus-circle",
    section: "main",
  },
  {
    label: "Dashboard",
    path: "/",
    icon: "home",
    section: "main",
  },
  {
    label: "Messaging",
    path: "/messaging",
    icon: "envelope",
    section: "main",
  },
  {
    label: "Calls",
    path: "/calls",
    icon: "phone",
    section: "main",
  },
  {
    label: "Calendar",
    path: "/calendar",
    icon: "calendar",
    section: "main",
  },
  {
    label: "Sales Hub",
    path: "/sales-hub",
    icon: "academic-cap",
    section: "main",
  },
  {
    label: "CRM",
    path: "/crm",
    icon: "building",
    section: "main",
  },
  {
    label: "Pages",
    path: "/pages",
    icon: "clipboard-document-list",
    section: "main",
    capabilityKey: "capability.pages",
  },
  // Browser (`/browser`) intentionally omitted from the rail — keep route in routes.config for deep links.
];

/** Grouped links for the workspace settings sidebar (`SettingsSideNav`). */
export type SettingsNavSection = {
  title: string;
  items: NavItem[];
};

export const settingsNavSections: SettingsNavSection[] = [
  {
    title: "Overview",
    items: [{ label: "Settings home", path: "/settings", section: "main" }],
  },
  {
    title: "Workspace",
    items: [
      { label: "Users & Roles", path: "/settings?tab=users", section: "main" },
      { label: "Email accounts", path: "/settings/email-accounts", section: "main" },
      { label: "Security", path: "/settings/security", section: "main" },
      { label: "Active sessions", path: "/settings/sessions", section: "main" },
    ],
  },
  {
    title: "Plans & usage",
    items: [
      { label: "AI usage", path: "/settings/ai-usage", section: "main" },
      { label: "Billing & plans", path: "/settings/billing", section: "main" },
    ],
  },
];

/** Flattened list for legacy consumers (e.g. search, tests). */
export const settingsNavItems: NavItem[] = settingsNavSections.flatMap((s) => s.items);

/**
 * Admin navigation items (super admin only)
 */
export const adminNavItems: NavItem[] = [
  {
    label: "Overview",
    path: "/super-admin",
    icon: "shield",
    section: "admin",
    roles: ["super_admin"],
  },
  {
    label: "Tenants",
    path: "/super-admin/tenants",
    icon: "office-building",
    section: "admin",
    roles: ["super_admin"],
  },
  {
    label: "Users",
    path: "/super-admin/users",
    icon: "users",
    section: "admin",
    roles: ["super_admin"],
  },
  {
    label: "System Settings",
    path: "/super-admin/settings",
    icon: "adjustments",
    section: "admin",
    roles: ["super_admin"],
  },
  {
    label: "Feature Flags",
    path: "/super-admin/features",
    icon: "flag",
    section: "admin",
    roles: ["super_admin"],
  },
];
